import {css, CSSResult, html, LitElement, nothing, unsafeCSS} from 'lit';
import {classMap} from 'lit/directives/class-map.js';
import {customElement, property} from 'lit/decorators.js';
import {consume} from '@lit/context';
import typographyStyles from '../../../styles/responsive-nui-typography.scss';
import styles from './upsert-collection-layer.scss';
import {CollectionTrackingContext, Messages, WishlistCollection} from '../../../@types/wishlist.js';
import {i18nContext} from '../../../scripts/context/i18nContext.js';
import {WishlistAppConfig, wishlistAppConfigContext} from '../../../scripts/context/appConfigContext.js';
import {collectionApi, CollectionApiError} from '../../../scripts/service/collection-api.js';
import {collectionsContext, productsContext} from '../../../scripts/context/wishlistContext.js';
import {CollectionsState, ProductsState} from '../../../scripts/context/wishlist-state.js';
import {collectionMenuContext} from '../../../scripts/context/menuContext.js';
import {CollectionMenuState} from '../../../scripts/context/menu-state.js';
import {pushToSnackbar} from '../../../scripts/service/snackbar-service.js';
import {formatImageUrl} from '../../../scripts/service/image-formatter.js';
import {getBackButton} from '../base-layer/base-layer.js';
import baseLayerStyles from '../base-layer/base-layer.scss';
import { BdkTextField } from '@breuni-ui/bdk-text-field';
import { safeDefine } from '@breuni-ui/safe-define';

safeDefine('bdk-ents-text-field', BdkTextField);

@customElement('ents-upsert-collection-layer')
export class UpsertCollectionLayer extends LitElement {
  @consume({context: i18nContext})
  private messages!: Messages;

  @consume({context: wishlistAppConfigContext})
  private wishlistAppConfig!: WishlistAppConfig;

  @consume({context: collectionMenuContext, subscribe: true})
  @property({attribute: false})
  private collectionMenuState!: CollectionMenuState;

  @consume({context: collectionsContext, subscribe: true})
  @property({attribute: false})
  private collectionsState!: CollectionsState;

  @consume({context: productsContext, subscribe: true})
  @property({attribute: false})
  private productsState!: ProductsState;

  @property({ type: Boolean })
  private hasErrors: boolean = false;

  static get styles(): CSSResult[] {
    return [css`${unsafeCSS(styles)}`, css`${unsafeCSS(typographyStyles)}`, css`${unsafeCSS(baseLayerStyles)}`];
  }

  private closeLayer = () => {
    if (this.collectionMenuState.context.openUpsertLayer) {
      this.collectionMenuState.closeUpsertLayer();
      this.handleErrorCodes([]);

      const form = this.shadowRoot!.querySelector('form');
      if (form) {
        form.reset();
      }
    }
  };

  private validateInput(event: Event) {
    const target = event.target as HTMLElement;
    const input = target.tagName === 'BDK-ENTS-TEXT-FIELD' ? target as BdkTextField : target.querySelector('input') as HTMLInputElement;
    const value = input?.value || (target as BdkTextField).value;
    if (value?.length > 0) {
      const errors = this.validateCollectionName(value);
      this.handleErrorCodes(errors);
    }
  }

  private validateCollectionName(collectionName: string) {
    const errorCodes: string[] = [];

    if (!collectionName || collectionName.trim().length === 0) {
      errorCodes.push(CollectionApiError['BLANK']);
    }

    if (collectionName.length > 50) {
      errorCodes.push(CollectionApiError['TOO_LONG']);
    }

    const regex = /^[\p{L}\p{N},!?\-'.\p{Zs}]*$/u;
    if (!regex.test(collectionName)) {
      errorCodes.push(CollectionApiError['INVALID']);
    }

    return errorCodes;
  }

  private handleErrorCodes(errorCodes: string[]) {
    this.hasErrors = errorCodes.length > 0;
    Object.values(CollectionApiError).forEach((errorCode) => {
      const errorMessage = this.shadowRoot!.getElementById(errorCode);
      if (errorMessage) {
        if (errorCodes.includes(errorCode)) {
          errorMessage.removeAttribute('hidden');
        } else {
          errorMessage.setAttribute('hidden', 'true');
        }
      }
    });
    // Trigger a re-render to update the invalid attribute
    this.requestUpdate();
  }

  private handleSubmit = async (event: Event) => {
    event.preventDefault();
    const form = this.shadowRoot!.querySelector('form')!;
    const formData = new FormData(form);

    const collectionName = formData.get('collection-name') as string;
    const errors = this.validateCollectionName(collectionName);
    this.handleErrorCodes(errors);
    if (errors.length > 0) {
      return; // Do not submit the form if there are validation errors
    }
    if (this.collectionMenuState.context.collection != null) {
      await this.updateCollection({
        ...this.collectionMenuState.context.collection,
        name: collectionName
      }, this.collectionMenuState.context.collectionTrackingContext!);
    } else {
      await this.addCollection(collectionName);
    }
  };

  private async updateCollection(collection: WishlistCollection, collectionTrackingContext: CollectionTrackingContext) {
    const result = await collectionApi.update(this.wishlistAppConfig.collectionEndpoint, collection);
    if (!result.isOk) {
      if (result?.errorCodes?.length) {
        this.handleErrorCodes(result.errorCodes);
      }
      return;
    }

    this.collectionsState.updateOnRename(result.collection!, collectionTrackingContext);
    this.closeLayer();
  }

  private async addCollection(collectionName: string) {
    const result = await collectionApi.add(this.wishlistAppConfig.collectionEndpoint, collectionName);
    if (!result.isOk) {
      if (result?.errorCodes?.length) {
        this.handleErrorCodes(result.errorCodes);
      }
      return;
    }

    const collection = result.collection!;
    if (this.collectionMenuState.context.productToAdd) {
      this.collectionsState.add(collection, 'all_articles');
      const product = this.collectionMenuState.context.productToAdd;
      await collectionApi.addItem(this.wishlistAppConfig.collectionEndpoint, collection.id, product.syan, product.colorId);
      this.productsState.addToCollection(product, collection, 'wishlist_action_menu');
      const formattedImage = formatImageUrl(product.defaultImageUrls[0].url, 'jpg');
      const truncatedCollectionName = collection.name.length <= 30 ? collection.name : `${collection.name.substring(0, 30)}...`;
      const title = `${this.messages['ents.wishlist.addToList.add-to-new-list']}`.replace('{}', truncatedCollectionName);
      pushToSnackbar(title, `https://cms.brnstc.de/product_images/287x393_retina${formattedImage}`);
    } else {
      this.collectionsState.add(collection, 'collection_list');
    }

    this.closeLayer();
  }

  private backButtonAction(){
    if (this.collectionMenuState.context.backButtonCallback){
      this.collectionMenuState.context.backButtonCallback();
    }
    this.closeLayer();
    this.dispatchEvent(new Event('ents-layer-back', {composed: true, bubbles: true}));
  }

  private renderErrorMessages(
    errorCode: string,
    errorMessageKeyPart: string,
  ) {
    const message = this.messages[`ents.wishlist.collection.upsert.error.${errorMessageKeyPart}`];
    return html`<p class="ents-copy-bold ents-upsert-collection-layer__error" id="${errorCode}" hidden>${message}</p>`;
  }

  private getCurrentErrorId(): string {
    const errorElement = this.shadowRoot?.querySelector('.ents-upsert-collection-layer__error:not([hidden])');
    return errorElement?.id ?? '';
  }

  protected render() {
    const isUpdate = this.collectionMenuState.context.collection != null;
    return html`
      <ents-base-layer
        drawer-test-id="ents-upsert-collection-layer"
        ?showLayer="${this.collectionMenuState.context.openUpsertLayer}"
        @ents-layer-closed="${this.closeLayer}"
      >
        ${this.collectionMenuState.context.backButtonCallback != undefined ?
                getBackButton(this.messages, 'ents-upsert-collection-layer', this.backButtonAction.bind(this)) : nothing}
        <div class="ents-upsert-collection-layer" data-test-ents-wishlist-collection-create-layer>
          <h2 slot="title">${isUpdate
            ? this.messages['ents.wishlist.collection.update.title']
            : this.messages['ents.wishlist.collection.create.title']}</h2>
          <form @submit="${this.handleSubmit}">
            <bdk-ents-text-field
              class="ents-upsert-collection-layer__collection-name ${classMap({'has-error': this.hasErrors})}"
              name="collection-name"
              type="text"
              maxlength="50"
              aria-describedby="${this.getCurrentErrorId()}"
              @input="${this.validateInput}"
              value="${isUpdate ? this.collectionMenuState.context.collection?.name ?? '' : ''}"
              placeholder="${isUpdate ? '' : this.messages['ents.wishlist.collection.name.placeholder']}"
              ?invalid="${this.hasErrors}"
            >
              ${this.messages['ents.wishlist.collection.name.label']}

              <div slot="error">
                ${this.renderErrorMessages(CollectionApiError['BLANK'], 'name.blank')}
                ${this.renderErrorMessages(CollectionApiError['TOO_LONG'], 'name.too-long')}
                ${this.renderErrorMessages(CollectionApiError['INVALID'], 'name.invalid')}
                ${this.renderErrorMessages(CollectionApiError['ALREADY_EXISTS'], 'name.already-exists')}
                ${this.renderErrorMessages(CollectionApiError['UNEXPECTED'], 'unexpected')}
              </div>
            </bdk-ents-text-field>
            <button type="submit"
                    class="bdk-button"
                    variant="primary"
                    size="medium">
              ${isUpdate
                ? this.messages['ents.wishlist.collection.update.button']
                : this.messages['ents.wishlist.collection.create.button']}
            </button>
          </form>
        </div>
      </ents-base-layer>`;
  }
}
